import {useState, useEffect, useRef, useCallback} from 'react'

// import {chatApi} from '../api/chatApi' // Commented out: Replaced by WebSocket
import {Message, StoredField} from '../types/chat'

/*const generateId = (): string =>
  `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`*/

// Global Audio element - try reusing this for better autoplay compatibility
const globalAudioElement = new Audio()
globalAudioElement.autoplay = false // We will call play() explicitly
// Pre-load a silent AAC snippet on initialization, hoping to satisfy browser policies after first interaction
// Note: Autoplay might still require a user gesture before this can be programmatically played.
globalAudioElement.src =
  'data:audio/aac;base64,SUQzBAAAAAABEVRYWFgAAAAtAAADY29tbWVudABCaWdTb3VuZEJhbmsuY29tIC8gTGFTb25vdGhlcXVlLm9yZwBURU5DAAAAHQAAA1N3aXRjaCBQbHVzIMKpIE5DSCBTb2Z0d2FyZQBUSVQyAAAABgAAAzIyMzUAVFNTRQAAAA8AAANMYXZmNTcuODMuMTAwAAAAAAAAAAAAAAD//tAwAAAAAAAAAAAAAAAAAAAAAAAB5AUAAwAAAAEDgAAAAAAAAAAAAA='

// Define callback types
/*interface ChatStateCallbacks {
  onAiResponseReceived?: (aiMessage: Message, setContinuous?: boolean) => void
}*/

/**
 * Custom hook that manages the chat state
 * Handles message history, user input, sending messages, and stored field tracking
 */
export function useChatState() {
  const [messages, setMessages] = useState<Message[]>([])
  const [inputMessage, setInputMessage] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [storedFields, setStoredFields] = useState<StoredField[]>([])
  const [conversationStarted, setConversationStarted] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const isMounted = useRef(true)
  // Ref to store the current object URL to revoke it later
  const currentObjectUrlRef = useRef<string | null>(null)

  /**
   * Update callback references.
   */
  /*const setCallbacks = useCallback((callbacks: ChatStateCallbacks) => {
    callbacksRef.current = callbacks
  }, [])*/

  /**
   * Stops the currently playing audio, cleans up listeners, and clears the reference.
   */
  const stopAudioPlayback = useCallback(() => {
    // Use the global audio element directly
    if (!globalAudioElement.paused) {
      // console.log('Stopping existing audio playback explicitly...')
      globalAudioElement.pause()
      // Reset src to potentially release resources, keep the silent track perhaps?
      // globalAudioElement.src = '' // Or reset to silent track?
    }

    // Revoke the object URL if it exists
    if (currentObjectUrlRef.current) {
      // console.log('Revoking object URL:', currentObjectUrlRef.current)
      URL.revokeObjectURL(currentObjectUrlRef.current)
      currentObjectUrlRef.current = null
      globalAudioElement.onerror = null

      // Clear the source to potentially help GC
      // Setting src to empty string or silent track might be better here
      // try {
      //   globalAudioElement.src = '';
      // } catch (e) {
      //   console.warn('Error clearing audio source:', e);
      // }

      // No need to null the ref as it points to the global constant element
      // currentAudioRef.current = null;
      // console.log('Audio playback stopped and object URL revoked.')
    }
  }, []) // Dependency array is empty as it only uses refs

  // Clean up when component unmounts
  useEffect(() => {
    isMounted.current = true
    return () => {
      isMounted.current = false
      // Stop any playing audio on unmount
      stopAudioPlayback()
    }
  }, [stopAudioPlayback]) // Now depends on stopAudioPlayback

  /**
   * Creates a message from the system (AI)
   */
  /*const createSystemMessage = useCallback(
    (content: string, audioData: Blob | null = null): Message => ({
      id: generateId(),
      content,
      isUser: false,
      audio: audioData,
      timestamp: Date.now(),
    }),
    []
  )*/

  /**
   * Creates a message from the user
   */
  /*const createUserMessage = useCallback(
    (content: string): Message => ({
      id: generateId(),
      content,
      isUser: true,
      timestamp: Date.now(),
    }),
    []
  )*/

  /**
   * Fetches the initial chat message and state from the API.
   * This should be called manually after the user selects the initial language.
   * @param languageName The language name selected by the user (e.g., "English")
   */
  /*const loadInitialChat = useCallback(
    async (languageName: LanguageName) => {
      // Prevent multiple initializations
      if (conversationStarted || isLoading) return

      setIsLoading(true)
      setError(null)
      try {
        // const data = await chatApi.initializeChat(languageName) // Commented out: Replaced by WebSocket
        // if (!isMounted.current) return
        // setCurrentField(data.response.current_field)
        // setStoredFieldsMap(data.response.stored_fields || {})
        // setCurrentStage(data.response.stage || 1)
        // setConversationHistory(data.response.conversation_history || [])
        // setCompletionMessageType(data.response.completion_message_type || 0)
        // setFacilityName(data.response.facility_name || null)
        // if (
        //   data.response.stored_field &&
        //   Array.isArray(data.response.stored_field)
        // ) {
        //   const initialStoredFields = data.response.stored_field
        //   setStoredFields(initialStoredFields)
        // }
        // if (data.response.content) {
        //   let audioData = null
        //   try {
        //     const ttsData = await chatApi.getTextToSpeech( // Commented out: Replaced by WebSocket
        //       data.response.content,
        //       languageName
        //     )
        //     audioData = ttsData.audio
        //   } catch (ttsError) {
        //   }
        //   const aiMessage = createSystemMessage(
        //     data.response.content,
        //     audioData
        //   )
        //   setMessages([aiMessage])
        //   callbacksRef.current.onAiResponseReceived?.(aiMessage)
        // }
        // setConversationStarted(true)
        // console.log('Initial chat loaded successfully.')
        // This function is now largely handled by the WebSocket 'initialState' message
        // and the initial AI greeting streamed from Gemini.
        // Consider removing or heavily refactoring if no longer directly called.
        // For now, it's commented to prevent HTTP calls.
      } catch (error) {
        // console.error('Error initializing chat:', error)

        if (!isMounted.current) return

        const errorMessage =
          'Sorry, there was an error initializing the chat. Please try again later.'
        setError(errorMessage)
        setMessages([createSystemMessage(errorMessage)])
      } finally {
        if (isMounted.current) {
          setIsLoading(false)
        }
      }
    },
    [
      conversationStarted,
      isLoading,
      createSystemMessage,
      setIsLoading,
      setError,
      setCurrentField,
      setStoredFieldsMap,
      setCurrentStage,
      setConversationHistory,
      setCompletionMessageType,
      setStoredFields,
      setMessages,
      setConversationStarted,
      setFacilityName,
    ]
  )*/

  /**
   * Plays audio data (text-to-speech) for a message
   *
   * @param audioBlob - Blob containing the raw audio data
   * @param onEnded - Optional callback for when audio playback ends
   * @returns The audio element that was created (or null if playback fails)
   */
  const playAudio = useCallback(
    async (
      audioBlob: Blob,
      onEnded?: () => void
    ): Promise<HTMLAudioElement | null> => {
      // Stop any currently playing audio before starting new playback
      stopAudioPlayback() // This also revokes previous object URL

      try {
        let currentlyPaused = false

        // Create an object URL from the Blob
        const objectUrl = URL.createObjectURL(audioBlob)
        // console.log('Created object URL:', objectUrl)
        currentObjectUrlRef.current = objectUrl // Store for revocation

        // Check if the same audio (identified by Object URL) is currently assigned to the global element
        if (
          !globalAudioElement.paused && // Check if playing
          globalAudioElement.src === objectUrl
        ) {
          // If the *same* audio is playing, pause it
          // console.log('Pausing current audio playback')
          globalAudioElement.pause()
          // Don't revoke URL or call onEnded when pausing the same track
          return globalAudioElement // Return the element in its paused state
        } else if (globalAudioElement.src === objectUrl) {
          // If the same audio is assigned but paused, resume it
          // console.log('Resuming paused audio playback')
          currentlyPaused = true
          await globalAudioElement.play()
        } else {
          // If a different audio or no audio is assigned, or it was paused,
          // we will proceed to assign the new src and play below.
          // The stopAudioPlayback call at the beginning handles stopping any *different* previous audio.
        }

        // If we are resuming, return the element
        if (currentlyPaused) {
          return globalAudioElement
        }

        // We are playing a new track (or replaying a previously stopped one)
        // console.log('Assigning new src and playing audio...')

        // Assign the new object URL to the global Audio element
        globalAudioElement.src = objectUrl

        // Explicitly prevent looping
        globalAudioElement.loop = false
        // Autoplay is false, we call play() explicitly
        // globalAudioElement.autoplay = false;
        // Preload is often default or handled by browser
        // globalAudioElement.preload = 'auto';

        // No need to store ref, we use the global element directly
        // currentAudioRef.current = globalAudioElement;

        // Set up ended handler - use a flag to ensure the callback is only called once
        let callbackFired = false

        const fireEndCallback = () => {
          if (callbackFired) return
          callbackFired = true

          // console.log('Audio playback complete or stopped')

          // Revoke the object URL when playback ends or is stopped
          if (currentObjectUrlRef.current === objectUrl) {
            // Only revoke if it's the URL we were playing
            /*console.log(
              'Revoking object URL on end:',
              currentObjectUrlRef.current
            )*/
            URL.revokeObjectURL(currentObjectUrlRef.current)
            currentObjectUrlRef.current = null
          }

          // No need to clear ref
          // if (currentAudioRef.current === globalAudioElement) {
          // Remove event listeners from the global element
          globalAudioElement.onended = null
          globalAudioElement.ontimeupdate = null
          globalAudioElement.onpause = null
          globalAudioElement.onplay = null
          globalAudioElement.onerror = null
          // }
          // Call the callback after a safe delay
          if (onEnded) {
            onEnded()
          }
        }

        // Set up ended handler for the global element
        globalAudioElement.onended = () => {
          // console.log('Audio ended event triggered')
          fireEndCallback()
        }

        // Also track progress on the global element
        globalAudioElement.ontimeupdate = () => {
          // Only check if we're near the end once we have valid duration info
          if (
            !callbackFired &&
            globalAudioElement.duration > 0 &&
            globalAudioElement.currentTime > 0 &&
            globalAudioElement.currentTime >= globalAudioElement.duration - 0.4
          ) {
            // console.log('Audio nearly complete, triggering end handler')
            fireEndCallback()
          }
        }

        // Handle errors on the global element
        globalAudioElement.onerror = (e) => {
          console.error(
            'Audio playback error:',
            e instanceof Error ? e.message : e
          )
          if (isMounted.current) {
            setError('Could not play audio. Please check your audio settings.')
          }
          // Ensure URL is revoked even on error
          fireEndCallback()
        }

        // Play the audio using the global element
        // console.log('Starting audio playback using global element')
        try {
          await globalAudioElement.play()
        } catch (error) {
          // console.error('Playback failed:', error)
          // Ensure URL is revoked if play fails immediately
          fireEndCallback()
        }

        // console.log('Awaited audio playback')
        return globalAudioElement // Return the global element
      } catch (error) {
        // console.error('Error setting up or playing audio:', error)
        if (isMounted.current) {
          setError('Could not play audio. Please check your audio settings.')
        }
        stopAudioPlayback() // Ensure cleanup on error
        if (onEnded) {
          onEnded()
        }
        return null
      }
    },
    [stopAudioPlayback, setError] // Added setError dependency
  )

  /**
   * Sends a user message to the API and handles the response
   * @param languageName The language name (e.g., "English")
   */
  /*const sendMessage = useCallback(
    async (
      inputedMessage: string,
      languageName: LanguageName,
      undoCurrentField: boolean = false,
      undoMessage: string | null = null
    ) => {

      if (!inputedMessage.trim()) return
      // Ensure chat has been initialized before sending messages
      // if (!conversationStarted) { // This check might still be relevant
      //   setError('Please initialize the chat first.') // Or handle differently
      //   return
      // }

      if (undoCurrentField === true && undoMessage != null) {
        const userMessage = createUserMessage(undoMessage)
        setMessages((prevMessages) => [...prevMessages, userMessage])
      } else {
        const userMessage = createUserMessage(inputedMessage)
        setMessages((prevMessages) => [...prevMessages, userMessage])
      }

      setInputMessage('')
      setIsLoading(true)
      setError(null)

      try {
        // const latencyStart = performance.now()

        // const data: ChatResponse = await chatApi.sendMessage( // Commented out: Replaced by WebSocket
        //   inputedMessage,
        //   currentField,
        //   currentStage,
        //   conversationHistory,
        //   storedFieldsMap,
        //   completionMessageType,
        //   languageName
        // )
        // if (!isMounted.current) return
        // let audioData: Blob | null = null
        // try {
        //   const ttsData = await chatApi.getTextToSpeech(data.response.content) // Commented out: Replaced by WebSocket
        //   audioData = ttsData.audio
        // } catch (ttsError) {
        // console.error('Error getting text-to-speech:', ttsError)
        // Continue without audio instead of failing the whole operation
        // }
        // if (!isMounted.current) return

            // Check if we're updating an existing field or adding a new one
            setStoredFields((prevFields) => {
              const existingFieldIndex = prevFields.findIndex(
                (field) => field.name === name
              )

              if (existingFieldIndex >= 0) {
                // Update existing field
                const newFields = [...prevFields]
                newFields[existingFieldIndex] = {name, value}
                return newFields
              } else {
                // Add new field
                return [...prevFields, {name, value}]
              }
            })
          }
        }

        const aiMessage = createSystemMessage(
          data.response.content,
          audioData,
          data.response.current_field_options,
          data.response.current_field
        )
        setMessages((prevMessages) => [...prevMessages, aiMessage])
        callbacksRef.current.onAiResponseReceived?.(aiMessage)
        // const latencyEnd = performance.now()
        // const latency = latencyEnd - latencyStart

        // This function is now primarily handled by `sendChatMessageViaWebSocket` in Chat.tsx
        // which sends the message via Action Cable. The response handling is done
        // in the `handleWebSocketMessage` callback in Chat.tsx.
        // For now, it's commented to prevent HTTP calls.
      } catch (error) {
        // console.error('Error sending message:', error)

        if (!isMounted.current) return

        const errorMessage =
          'Sorry, there was an error processing your request. Please try again.'
        setError(errorMessage)
        setMessages((prevMessages) => [
          ...prevMessages,
          createSystemMessage(errorMessage),
        ])
      } finally {
        if (isMounted.current) {
          setIsLoading(false)
        }
      }
    },
    [
      conversationStarted, // Added dependency
      currentField,
      currentStage,
      conversationHistory,
      storedFieldsMap,
      completionMessageType,
      createUserMessage,
      createSystemMessage,
      // State setters:
      setInputMessage,
      setIsLoading,
      setError,
      setCurrentField,
      setStoredFieldsMap,
      setCurrentStage,
      setConversationHistory,
      setCompletionMessageType,
      setStoredFields,
      setMessages,
      setCurrentFieldOptions,
    ]
  )*/

  /**
   * Resets the chat to its initial state
   * NOTE: This might need adjustment depending on whether you want it to
   * trigger the initial language selection flow again. Currently, it resets
   * but doesn't re-trigger the initial flow.
   */
  const clearChat = useCallback(() => {
    if (!isMounted.current) return

    // Stop any playing audio and revoke URL before clearing state
    stopAudioPlayback()

    setMessages([])
    setStoredFields([])
    setConversationStarted(false) // Mark as not started
    setError(null)
    // Maybe reset input message?
    setInputMessage('')
    // Reset loading state?
    setIsLoading(false)

    // Consider if language state should be reset here or handled in the calling component
  }, [stopAudioPlayback /* State setters */])

  return {
    messages,
    storedFields,
    isLoading,
    inputMessage,
    error,
    conversationStarted, // Export conversationStarted if needed elsewhere
    setInputMessage,
    // sendMessage, // Commented out: Replaced by WebSocket
    playAudio,
    stopAudioPlayback,
    clearChat,
    // loadInitialChat, // Commented out: Replaced by WebSocket
    // facilityName, // Export the updated function
  }
}
